// @FixTypeLater
// @ts-nocheck

import React from 'react';
import type { ComponentType } from 'react';

/* Styled Components */
import {
  Wrapper,
  ErrorMessage,
  InputWrapper,
  WishTextRightSideWrapper,
  RightSubText,
} from 'styles/common/WishTextInput';
import { FormElementTitle } from 'styles/common/FormElement';

/* Constants */
import KEYCODES from 'constants/KeyCodeConstants';

export type WishTextInputSizeType = ('small' | 'splash') | null | undefined;

export type InjectedPropsType = {
  type: string;
  onKeyDown: (arg0: React.KeyboardEvent<HTMLInputElement>) => void;
  setRef?: (arg0: HTMLInputElement | null | undefined) => void;
  padding: string;
  isErrorShown?: boolean;
  disabled?: boolean;
  size?: WishTextInputSizeType;
};

type HocOnlyPropsTypes = {
  focus?: boolean;
  onUpPressed?: () => void;
  onDownPressed?: () => void;
  onLeftPressed?: (arg0: React.KeyboardEvent<HTMLInputElement>) => void;
  onRightPressed?: (arg0: React.KeyboardEvent<HTMLInputElement>) => void;
  onEnterPressed?: () => void;
  onDeletePressed?: () => void;
  onFocus?: () => void;
  setRef?: (arg0: HTMLInputElement | null | undefined) => void;
  size?: WishTextInputSizeType;
  padding?: string;
  isErrorShown?: boolean;
  type?: string;
  disabled?: boolean;
  stretch?: boolean;
  margin?: string;
  errorMessage?: string;
  width?: number;
  height?: string;
  title?: string;
  titleBold?: boolean;
  rightSubText?: string;
  rightSideElement?: React.ReactNode;
};

type StateType = EmptyObject;

const WishTextInputHOC = <P extends InjectedPropsType = InjectedPropsType>(
  WrappedComponent: ComponentType<P>,
): ComponentType<Omit<P, keyof InjectedPropsType> & HocOnlyPropsTypes> =>
  class extends React.Component<
    Omit<P, keyof InjectedPropsType> & HocOnlyPropsTypes,
    StateType
  > {
    wishTextInputRef: HTMLInputElement;

    componentDidMount() {
      const { focus } = this.props;
      if (this.wishTextInputRef && focus) {
        window.setTimeout(() => {
          if (this.wishTextInputRef) {
            this.wishTextInputRef.focus();
          }
        }, 0);
      }
    }

    onKeyPressed = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const {
        onEnterPressed,
        onUpPressed,
        onDownPressed,
        onLeftPressed,
        onRightPressed,
        onDeletePressed,
      } = this.props;
      if (event.keyCode === KEYCODES.ENTER && onEnterPressed) {
        onEnterPressed();
      } else if (event.keyCode === KEYCODES.UP && onUpPressed) {
        onUpPressed();
        event.preventDefault();
      } else if (event.keyCode === KEYCODES.DOWN && onDownPressed) {
        onDownPressed();
        event.preventDefault();
      } else if (event.keyCode === KEYCODES.LEFT && onLeftPressed) {
        onLeftPressed(event);
        event.preventDefault();
      } else if (event.keyCode === KEYCODES.RIGHT && onRightPressed) {
        onRightPressed(event);
        event.preventDefault();
      } else if (event.keyCode === KEYCODES.DELETE && onDeletePressed) {
        onDeletePressed();
      }
    };

    setWishTextInputRef = (component: HTMLInputElement) => {
      const { setRef } = this.props;
      if (setRef) {
        setRef(component);
      }
      this.wishTextInputRef = component;
    };

    render() {
      const {
        errorMessage,
        isErrorShown,
        margin,
        stretch,
        width,
        title,
        padding = '12px',
        size,
        titleBold,
        onUpPressed: _,
        onDownPressed: _1,
        onDeletePressed: _2,
        onEnterPressed: _3,
        disabled,
        rightSubText,
        type,
        rightSideElement,
        ...passedProps
      } = this.props;

      return (
        <Wrapper margin={margin} stretch={stretch} width={width}>
          {Boolean(title) && (
            <FormElementTitle
              size={size}
              bold={titleBold}
              isErrorShown={isErrorShown}
            >
              {title}
            </FormElementTitle>
          )}
          <InputWrapper hasRightSideElement={Boolean(rightSideElement)}>
            <WrappedComponent
              type={type || 'text'}
              onKeyDown={this.onKeyPressed}
              padding={padding}
              setRef={this.setWishTextInputRef}
              isErrorShown={isErrorShown}
              disabled={disabled}
              size={size}
              {...passedProps}
            />
            {rightSideElement && (
              <WishTextRightSideWrapper>
                {rightSideElement}
              </WishTextRightSideWrapper>
            )}
          </InputWrapper>
          {rightSubText ? <RightSubText>{rightSubText}</RightSubText> : null}
          {errorMessage && isErrorShown && (
            <ErrorMessage>{errorMessage}</ErrorMessage>
          )}
        </Wrapper>
      );
    }
  };

export default WishTextInputHOC;
